/**
 * Rotates an image by a specified angle
 * @param {HTMLImageElement|string} image - The original image element or data URL
 * @param {number} angle - The rotation angle in degrees
 * @param {HTMLCanvasElement} canvas - The canvas element to use for rotation
 * @returns {Promise<string>} - Promise resolving to the rotated image data URL
 */
export const rotateImage = (image, angle, canvas) => {
  return new Promise((resolve, reject) => {
    try {
      // If image is a data URL, load it first
      if (typeof image === 'string') {
        const img = new Image();
        img.onload = () => {
          resolve(rotateImageHelper(img, angle, canvas));
        };
        img.onerror = reject;
        img.src = image;
      } else {
        // Use the image directly
        resolve(rotateImageHelper(image, angle, canvas));
      }
    } catch (error) {
      reject(error);
    }
  });
};

/**
 * Helper function to perform the actual rotation
 * @param {HTMLImageElement} img - The image element
 * @param {number} angle - The rotation angle in degrees
 * @param {HTMLCanvasElement} canvas - The canvas element
 * @returns {string} - The rotated image data URL
 */
const rotateImageHelper = (img, angle, canvas) => {
  // Normalize angle to 0-360
  angle = ((angle % 360) + 360) % 360;
  
  // Check if we need to swap dimensions (for 90/270 degrees)
  const swapDimensions = angle === 90 || angle === 270;
  
  // Set canvas dimensions
  canvas.width = swapDimensions ? img.height : img.width;
  canvas.height = swapDimensions ? img.width : img.height;
  
  const ctx = canvas.getContext('2d');
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  
  // Move to center of canvas
  ctx.translate(canvas.width / 2, canvas.height / 2);
  
  // Rotate
  ctx.rotate((angle * Math.PI) / 180);
  
  // Draw the image back, centered
  ctx.drawImage(
    img,
    -img.width / 2,
    -img.height / 2,
    img.width,
    img.height
  );
  
  // Reset transformation
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  
  // Return data URL
  return canvas.toDataURL('image/png');
};

/**
 * Flips an image horizontally and/or vertically
 * @param {HTMLImageElement|string} image - The original image element or data URL
 * @param {boolean} flipHorizontal - Whether to flip horizontally
 * @param {boolean} flipVertical - Whether to flip vertically
 * @param {HTMLCanvasElement} canvas - The canvas element to use for flipping
 * @returns {Promise<string>} - Promise resolving to the flipped image data URL
 */
export const flipImage = (image, flipHorizontal, flipVertical, canvas) => {
  return new Promise((resolve, reject) => {
    try {
      // If image is a data URL, load it first
      if (typeof image === 'string') {
        const img = new Image();
        img.onload = () => {
          resolve(flipImageHelper(img, flipHorizontal, flipVertical, canvas));
        };
        img.onerror = reject;
        img.src = image;
      } else {
        // Use the image directly
        resolve(flipImageHelper(image, flipHorizontal, flipVertical, canvas));
      }
    } catch (error) {
      reject(error);
    }
  });
};

/**
 * Helper function to perform the actual flipping
 * @param {HTMLImageElement} img - The image element
 * @param {boolean} flipHorizontal - Whether to flip horizontally
 * @param {boolean} flipVertical - Whether to flip vertically
 * @param {HTMLCanvasElement} canvas - The canvas element
 * @returns {string} - The flipped image data URL
 */
const flipImageHelper = (img, flipHorizontal, flipVertical, canvas) => {
  // Set canvas dimensions
  canvas.width = img.width;
  canvas.height = img.height;
  
  const ctx = canvas.getContext('2d');
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  
  // Set the transformation
  ctx.translate(
    flipHorizontal ? canvas.width : 0,
    flipVertical ? canvas.height : 0
  );
  ctx.scale(
    flipHorizontal ? -1 : 1,
    flipVertical ? -1 : 1
  );
  
  // Draw the image with the flip transformation
  ctx.drawImage(img, 0, 0, img.width, img.height);
  
  // Reset transformation
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  
  // Return data URL
  return canvas.toDataURL('image/png');
}; 